// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';

import {
  Paper,
  TextareaAutosize,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import { ReactComponent as ExpandMoreIcon } from '../../../../assets/Icons/lucideIcons/chevron-down.svg';
import { ReactComponent as ExpandLessIcon } from '../../../../assets/Icons/lucideIcons/chevron-up.svg';
import { momentTz } from '../../../../utils/dates';
import {
  useTableDispatch,
  useTableState,
} from '../../context/TableSchemaBuilderContext';
import { useCellManagement } from '../../hooks/useCellManagement';
import { DATE_FORMAT } from '../../../../const/CommonConst';
import { isValueEmpty } from '../../../../utils/core';

const CellPreviewer = () => {
  const { schema, ui, config } = useTableState();
  const dispatch = useTableDispatch();
  const { handleCellChange } = useCellManagement();
  const theme = useTheme();

  const [isExpanded, setIsExpanded] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const currentCellId = ui?.currentCell;
  const currentCellValue = schema?.cells?.[currentCellId]?.value ?? '';
  const isCurrentCellLocked = schema?.cells?.[currentCellId]?.isLocked ?? false;
  const isCurrentCellFormulaType =
    schema?.cells?.[currentCellId]?.valueType === 'formula';
  const currentCellDataType =
    schema?.cells?.[currentCellId]?.dataType ?? 'text';
  const isCurrentCellDateType = currentCellDataType === 'date';

  useEffect(() => {
    let newCellValue = currentCellValue;
    if (isCurrentCellDateType && !isValueEmpty(currentCellValue)) {
      newCellValue = momentTz(new Date(currentCellValue)).format(DATE_FORMAT);
    }
    setLocalValue(newCellValue);
  }, [currentCellId, currentCellValue, isCurrentCellDateType]);

  const handlePreviewerFocus = useCallback(() => {
    dispatch({ type: 'SET_EDIT_MODE', payload: false });
  }, [dispatch]);

  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      let formattedValue = newValue;
      if (isCurrentCellDateType) {
        formattedValue = momentTz(new Date(newValue)).format(DATE_FORMAT);
      }
      setLocalValue(formattedValue);
      // Update cell value immediately as user types
      if (currentCellId) {
        handleCellChange(currentCellId, newValue);
      }
    },
    [currentCellId, handleCellChange, isCurrentCellDateType]
  );

  const handleBlur = useCallback(() => {
    if (currentCellId && localValue !== currentCellValue) {
      handleCellChange(currentCellId, localValue);
    }
  }, [currentCellId, localValue, currentCellValue, handleCellChange]);

  const handleExpandToggle = useCallback(() => {
    setIsExpanded((prevExpanded) => !prevExpanded);
  }, [setIsExpanded]);

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderBottom: `1px solid ${alpha(theme?.palette?.primary?.main, 0.2)}`,
        backgroundColor: alpha(theme?.palette?.primary?.main, 0.05),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
          minHeight: '36px',
        }}
      >
        <div
          style={{
            marginRight: '8px',
            color: alpha(theme?.palette?.primary?.main, 0.31),
            fontWeight: 500,
            minWidth: '50px',
          }}
        >
          {currentCellId ?? ''}
        </div>
        <TextareaAutosize
          disabled={
            isCurrentCellFormulaType ||
            isCurrentCellLocked ||
            config?.readOnly ||
            isCurrentCellDateType
          }
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          spellCheck={true}
          onFocus={handlePreviewerFocus}
          placeholder="Enter cell value"
          style={{
            flex: 1,
            border: `1px solid ${alpha(theme?.palette?.primary?.main, 0.1)}`,
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '14px',
            fontFamily: 'inherit',
            resize: 'none',
            backgroundColor: theme?.palette?.other?.white,
            minHeight: isExpanded ? '100px' : '24px',
            maxHeight: isExpanded ? '300px' : '32px',
            overflow: isExpanded ? 'auto' : 'hidden',
            transition: 'all 0.2s ease',
          }}
        />
        <IconButton size="small" onClick={handleExpandToggle} sx={{ ml: 1 }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>
    </Paper>
  );
};

export default CellPreviewer;
